import { Component, Input, OnInit } from '@angular/core';
import { PopoverController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { ConfluencePage, ConfluenceSpace } from '../../../core/models/knowledge-confluence.model';
import { extractErrorResponse, extractHttpStatus } from '../../../core/models/knowledge-error-response.model';
import { RESPONSE_STATUS } from '../../../core/constants/enums';
import { ToastService } from '../../../coding/refactor/services/toast.service';
import { ConfluenceService } from '../../../no-coding/knowledge/services/confluence.service';

export type ConfluenceViewStep = 'spaces' | 'pages' | 'preview';

/** Nodo del árbol de páginas reconstruido en memoria a partir de parentId. */
export interface ConfluencePageNode {
  page: ConfluencePage;
  children: ConfluencePageNode[];
  depth: number;
}

/**
 * Navegador de Confluence (RF-001/RF-010): espacios → páginas (árbol por
 * parentId reconstruido en cliente, OQ-001) → preview del recuento antes de
 * importar. Navegar solo requiere autenticación (BR-13); el botón "Importar"
 * únicamente se muestra a Manager+ (BR-12), vía `isManagerOrAbove`.
 * El backoff ante 429 es del backend: si llega un 429 aquí es el error
 * explícito tras agotarlo y se muestra sin resultados parciales (NFR-03).
 */
@Component({
  selector: 'app-confluence-navigator',
  templateUrl: './confluence-navigator.component.html',
  styleUrls: ['./confluence-navigator.component.scss'],
  standalone: false
})
export class ConfluenceNavigatorComponent implements OnInit {
  @Input() projectId!: string;
  @Input() isManagerOrAbove = false;

  viewStep: ConfluenceViewStep = 'spaces';
  loading = false;
  /** No hay conexión Confluence configurada (424 CONNECTION_MISSING). */
  connectionMissing = false;
  /** La carga del paso actual falló: se muestra error, no el estado vacío. */
  stepLoadFailed = false;

  spaces: ConfluenceSpace[] = [];
  selectedSpace: ConfluenceSpace | null = null;

  /** Páginas planas cargadas del espacio (acumuladas por paginación). */
  pages: ConfluencePage[] = [];
  /** Árbol reconstruido en memoria desde parentId. */
  pageTree: ConfluencePageNode[] = [];
  /** Filas visibles (árbol aplanado según nodos expandidos). */
  visibleRows: ConfluencePageNode[] = [];
  /** Todos los nodos del árbol (para búsqueda en todos los niveles). */
  allNodes: ConfluencePageNode[] = [];
  private expanded = new Set<string>();
  selectedPageIds = new Set<string>();

  searchQuery = '';

  /** Selección expandida a descendientes, mostrada en la vista preview. */
  pagesToImportPreview: ConfluencePage[] = [];

  constructor(
    private popoverController: PopoverController,
    private confluenceService: ConfluenceService,
    private toastService: ToastService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.loadSpaces();
  }

  loadSpaces(): void {
    this.loading = true;
    this.viewStep = 'spaces';
    this.connectionMissing = false;
    this.searchQuery = '';
    this.confluenceService.listSpaces(this.projectId).subscribe({
      next: (list) => {
        this.spaces = list || [];
        this.loading = false;
      },
      error: (err) => {
        this.spaces = [];
        this.loading = false;
        this.connectionMissing =
          extractHttpStatus(err) === 424 &&
          extractErrorResponse(err)?.code === 'CONNECTION_MISSING';
        // Con conexión ausente el estado vacío del modal ya lo explica; el
        // toast solo aporta ruido.
        if (!this.connectionMissing) {
          this.showNavigationError(err);
        }
      }
    });
  }

  selectSpace(space: ConfluenceSpace): void {
    this.selectedSpace = space;
    this.pages = [];
    this.pageTree = [];
    this.visibleRows = [];
    this.allNodes = [];
    this.expanded.clear();
    this.selectedPageIds.clear();
    this.searchQuery = '';
    this.viewStep = 'pages';
    this.loadPages();
  }

  private readonly PAGE_SIZE = 100;

  private loadPages(): void {
    if (!this.selectedSpace) return;
    this.loading = true;
    this.stepLoadFailed = false;
    this.pages = [];
    this.loadPagesBatch(0);
  }

  private loadPagesBatch(page: number): void {
    this.confluenceService.listPages(this.projectId, this.selectedSpace!.key, page, this.PAGE_SIZE).subscribe({
      next: (list) => {
        const batch = list || [];
        this.pages = [...this.pages, ...batch];
        if (batch.length >= this.PAGE_SIZE) {
          this.loadPagesBatch(page + 1);
        } else {
          this.rebuildTree();
          this.loading = false;
        }
      },
      error: (err) => {
        this.pages = [];
        this.pageTree = [];
        this.visibleRows = [];
        this.allNodes = [];
        this.loading = false;
        this.stepLoadFailed = true;
        this.showNavigationError(err);
      }
    });
  }

  /** Reconstruye el árbol en memoria desde la lista plana (parentId). */
  private rebuildTree(): void {
    const nodesById = new Map<string, ConfluencePageNode>();
    for (const page of this.pages) {
      nodesById.set(page.pageId, { page, children: [], depth: 0 });
    }
    const roots: ConfluencePageNode[] = [];
    for (const node of nodesById.values()) {
      const parentId = node.page.parentId;
      const parent = parentId ? nodesById.get(parentId) : undefined;
      if (parent) {
        parent.children.push(node);
      } else {
        // Raíz del espacio o padre fuera del lote cargado.
        roots.push(node);
      }
    }
    const assignDepth = (nodes: ConfluencePageNode[], depth: number) => {
      for (const node of nodes) {
        node.depth = depth;
        assignDepth(node.children, depth + 1);
      }
    };
    assignDepth(roots, 0);
    this.pageTree = roots;
    this.allNodes = Array.from(nodesById.values());
    this.refreshVisibleRows();
  }

  private refreshVisibleRows(): void {
    const rows: ConfluencePageNode[] = [];
    const walk = (nodes: ConfluencePageNode[]) => {
      for (const node of nodes) {
        rows.push(node);
        if (this.isExpanded(node)) walk(node.children);
      }
    };
    walk(this.pageTree);
    this.visibleRows = rows;
  }

  isExpanded(node: ConfluencePageNode): boolean {
    return this.expanded.has(node.page.pageId);
  }

  toggleExpand(node: ConfluencePageNode): void {
    if (node.children.length === 0) return;
    if (this.expanded.has(node.page.pageId)) {
      this.expanded.delete(node.page.pageId);
    } else {
      this.expanded.add(node.page.pageId);
    }
    this.refreshVisibleRows();
  }

  toggleSelection(node: ConfluencePageNode): void {
    if (this.selectedPageIds.has(node.page.pageId)) {
      this.selectedPageIds.delete(node.page.pageId);
    } else {
      this.selectedPageIds.add(node.page.pageId);
    }
  }

  isSelected(node: ConfluencePageNode): boolean {
    return this.selectedPageIds.has(node.page.pageId);
  }

  /** Selección + descendientes (importación por página+descendientes, OQ-001). */
  private collectSelectionWithDescendants(): ConfluencePage[] {
    const result = new Map<string, ConfluencePage>();
    const collect = (node: ConfluencePageNode) => {
      result.set(node.page.pageId, node.page);
      node.children.forEach(collect);
    };
    const visit = (nodes: ConfluencePageNode[]) => {
      for (const node of nodes) {
        if (this.selectedPageIds.has(node.page.pageId)) {
          collect(node);
        } else {
          visit(node.children);
        }
      }
    };
    visit(this.pageTree);
    return Array.from(result.values());
  }

  get filteredSpaces(): ConfluenceSpace[] {
    const q = this.searchQuery.trim().toLowerCase();
    if (!q) return this.spaces;
    return this.spaces.filter(s =>
      s.name.toLowerCase().includes(q) || s.key.toLowerCase().includes(q)
    );
  }

  get filteredVisibleRows(): ConfluencePageNode[] {
    const q = this.searchQuery.trim().toLowerCase();
    if (!q) return this.visibleRows;
    return this.allNodes.filter(node =>
      node.page.title.toLowerCase().includes(q)
    );
  }

  showPreview(): void {
    if (this.selectedPageIds.size === 0) {
      this.toastService.showToastWithTranslation(
        'KNOWLEDGE_FEATURE.CONFLUENCE.TOASTS.SELECT_PAGES',
        RESPONSE_STATUS.WARNING
      );
      return;
    }
    this.pagesToImportPreview = this.collectSelectionWithDescendants();
    this.searchQuery = '';
    this.viewStep = 'preview';
  }

  goBackFromPreview(): void {
    this.pagesToImportPreview = [];
    this.searchQuery = '';
    this.viewStep = 'pages';
  }

  confirmImport(): void {
    if (!this.selectedSpace || this.pagesToImportPreview.length === 0) return;
    const spaceKey = this.selectedSpace.key;
    const pageIds = this.pagesToImportPreview.map((p) => p.pageId);
    this.loading = true;
    const request$ = pageIds.length === 1
      ? this.confluenceService.importPage({ projectId: this.projectId, spaceKey, pageId: pageIds[0] })
      : this.confluenceService.importPagesBatch({ projectId: this.projectId, spaceKey, pageIds });
    request$.subscribe({
      next: (result) => {
        this.loading = false;
        const status = (result.failures?.length || result.capped)
          ? RESPONSE_STATUS.WARNING
          : RESPONSE_STATUS.OK;
        this.toastService.showToast(
          this.translateService.instant('KNOWLEDGE_FEATURE.CONFLUENCE.TOASTS.IMPORT_RESULT', {
            processed: result.processed,
            requested: result.requested
          }),
          status
        );
        this.popoverController.dismiss({ result: true, imported: result });
      },
      error: (err) => {
        this.loading = false;
        this.showNavigationError(err, 'KNOWLEDGE_FEATURE.CONFLUENCE.TOASTS.IMPORT_ERROR');
      }
    });
  }

  cancel(): void {
    this.popoverController.dismiss({ result: false });
  }

  /**
   * Errores de navegación/importación: 424 = sin conexión Confluence del
   * usuario; 429 = rate-limit persistente tras el backoff del backend. En el
   * resto se muestra el mensaje del ErrorResponse o el genérico.
   */
  private showNavigationError(err: unknown, fallbackKey = 'KNOWLEDGE_FEATURE.CONFLUENCE.TOASTS.LOAD_ERROR'): void {
    const status = extractHttpStatus(err);
    let message: string;
    if (status === 424) {
      // Un 424 distingue causas por `code`: credencial inválida/sin permisos
      // (PAT_INVALID) no es lo mismo que conexión ausente (CONNECTION_MISSING).
      const code = extractErrorResponse(err)?.code;
      message = this.translateService.instant(
        code === 'PAT_INVALID'
          ? 'KNOWLEDGE_FEATURE.CONFLUENCE.TOASTS.PAT_INVALID'
          : 'KNOWLEDGE_FEATURE.CONFLUENCE.TOASTS.NO_CONNECTION'
      );
    } else if (status === 429) {
      message = this.translateService.instant('KNOWLEDGE_FEATURE.CONFLUENCE.TOASTS.RATE_LIMITED');
    } else {
      message = extractErrorResponse(err)?.message || this.translateService.instant(fallbackKey);
    }
    this.toastService.showToast(message, RESPONSE_STATUS.ERROR);
  }
}
